import { getSystemList } from "@iewc/Services/OrderService";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import toggleFiltersOpen from "@insite/client-framework/Store/Pages/RfqMyQuotes/Handlers/ToggleFiltersOpen";
import updateSearchFields from "@insite/client-framework/Store/Pages/RfqMyQuotes/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon";
import Filter from "@insite/mobius/Icons/Filter";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    session: getSession(state),
    quotesDataView: getQuotesDataView(state, state.pages.rfqMyQuotes.getQuotesParameter),
    getQuotesParameter: state.pages.rfqMyQuotes.getQuotesParameter,
    jobQuoteEnabled: getSettingsCollection(state).quoteSettings.jobQuoteEnabled,

    /* Verndale Custom Feature - New variables */
    filtersOpen: state.pages.rfqMyQuotes.filtersOpen,
});

const mapDispatchToProps = {
    toggleFiltersOpen,
    updateSearchFields,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqMyQuotesHeaderStyles {
    container?: GridContainerProps;
    statusFilterGridItem?: GridItemProps;
    statusSelect?: SelectPresentationProps;
    typeFilterGridItem?: GridItemProps;
    typeSelect?: SelectPresentationProps;
    quoteCountGridItem?: GridItemProps;
    quoteCountText?: TypographyPresentationProps;
    emptyGridItem?: GridItemProps;
    toggleFilterGridItem?: GridItemProps;
    /** @deprecated Use toggleFilterButtonIcon instead. */
    toggleFilterIcon?: IconProps;
    toggleFilterButton?: ButtonPresentationProps;
    toggleFilterButtonIcon?: ButtonIconPresentationProps;
}

export const rfqMyQuotesHeaderStyles: RfqMyQuotesHeaderStyles = {
    container: {
        gap: 8,
        css: css`
            padding-bottom: 10px;
        `,
    },
    statusFilterGridItem: {
        width: [12, 6, 4, 3, 2],
    },
    typeFilterGridItem: {
        width: [12, 6, 4, 3, 2],
    },
    /* Verndale Custom Feature - Change style for count grid item */
    quoteCountGridItem: {
        width: 6,
        style: { marginTop: "8px" },
        align: "middle",
    },
    quoteCountText: { weight: 800 },
    /* Verndale Custom Feature - Change style for toogle filter grid item */
    toggleFilterGridItem: {
        width: 6,
        style: { marginTop: "8px", justifyContent: "flex-end" },
        align: "middle",
    },
    /* Verndale Custom Feature - Change style for toogle filter button */
    toggleFilterButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
        `,
    },
    toggleFilterButtonIcon: {
        src: Filter,
        size: 24,
    },
};

const styles = rfqMyQuotesHeaderStyles;

const RfqMyQuotesHeader = ({
    id,
    session,
    quotesDataView,
    getQuotesParameter,
    jobQuoteEnabled,
    toggleFiltersOpen,
    updateSearchFields,

    /* Verndale Custom Feature - New variables */
    filtersOpen,
}: Props) => {
    const quotesCount =
        quotesDataView.value && quotesDataView.pagination ? quotesDataView.pagination.totalItemCount : 0;

    const statusParemter = (getQuotesParameter as any).status;

    const [status, setStatus] = useState(statusParemter && statusParemter.length > 0 ? statusParemter[0] : "");
    /* Verndale Custom Feature - New variables */
    const [statuses, setStatuses] = useState([]);

    const statusChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        /* Verndale Custom Feature - Change filter value */
        updateSearchFields({ status: event.target.value ? [event.target.value] : undefined } as any);
    };

    const [type, setType] = useState(getQuotesParameter.types || "");
    const typeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newType = event.target.value;
        if (!newType) {
            updateSearchFields({ types: undefined });
        } else if (newType === "quote" || newType === "job") {
            updateSearchFields({ types: newType });
        }
    };

    useEffect(() => {
        const statusParemter = (getQuotesParameter as any).status;
        setStatus(statusParemter && statusParemter.length > 0 ? statusParemter[0] : "");
        setType(getQuotesParameter.types || "");
    }, [getQuotesParameter]);

    /* Verndale Custom Feature - START - Call fetch system list for quote statuses */

    const fetchStatusesFromSystemLists = async () => {
        const items = (await getSystemList({ namelist: "QuoteStatuses" })) as any;
        // Verndale Custom Feature start - rearrange items to be ordered by label, the order should be open > completed > expired > rest if any
        const order = ["Open", "Completed", "Expired"];
        items?.SystemList?.sort((a: any, b: any) => {
            return order.indexOf(a.Description) - order.indexOf(b.Description);
        });
        // Verndale Custom Feature start - rearrange items to be ordered by label, the order should be open > completed > expired > rest if any

        setStatuses(items?.SystemList?.map?.((x: any) => ({ label: x.Description, value: x.Name })) || []);
    };

    useEffect(() => {
        fetchStatusesFromSystemLists();
    }, []);
    /* Verndale Custom Feature - END */

    return (
        <>
            <Zone contentId={id} zoneName="Content00" />
            <GridContainer {...styles.container}>
                <GridItem {...styles.statusFilterGridItem}>
                    <Select
                        {...styles.statusSelect}
                        label={translate("Status")}
                        value={status}
                        onChange={statusChangeHandler}
                    >
                        <option value="">{translate("Select")}</option>
                        {/* Verndale Custom Feature - Sow statuses from system list */}
                        {statuses.map((status: any) => (
                            <option key={status.value} value={status.value}>
                                {status.label}
                            </option>
                        ))}
                    </Select>
                </GridItem>
                {jobQuoteEnabled && (
                    <GridItem {...styles.typeFilterGridItem}>
                        <Select
                            {...styles.typeSelect}
                            label={translate("Quote Type")}
                            value={type}
                            onChange={typeChangeHandler}
                        >
                            <option value="">{translate("Select")}</option>
                            <option value="quote">{translate("Sales Quotes")}</option>
                            <option value="job">{translate("Job Quotes")}</option>
                        </Select>
                    </GridItem>
                )}
            </GridContainer>

            <GridContainer>
                <GridItem {...styles.quoteCountGridItem}>
                    <Typography {...styles.quoteCountText} data-test-selector="rfqMyQuotes_count">
                        {quotesCount === 1 && translate("{0} Quote", quotesCount.toString())}
                        {quotesCount > 1 && translate("{0} Quotes", quotesCount.toString())}
                    </Typography>
                </GridItem>
                <GridItem {...styles.toggleFilterGridItem}>
                    <Button
                        {...styles.toggleFilterButton}
                        onClick={() => toggleFiltersOpen()}
                        data-test-selector="orderHistory_showHideFilters"
                    >
                        {/* Verndale Custom Feature - Remove OOTB aria + icon and set translation */}
                        {translate(filtersOpen ? "Hide search options" : "Show search options")}
                    </Button>
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RfqMyQuotesHeader as any) as any,
    definition: {
        group: "RFQ My Quotes",
        displayName: "Page Header",
        allowedContexts: ["RfqMyQuotesPage"],
    },
};

export default widgetModule;
